import React, { useState, useRef, useEffect } from 'react';

const SystemArchitecturePDT: React.FC = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);

  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [activeHotspot, setActiveHotspot] = useState<string | null>(null);
  const diagramContainerRef = useRef<HTMLDivElement>(null);

  // Calibration mode state
  const [calibrationMode, setCalibrationMode] = useState(false);
  const [editingHotspot, setEditingHotspot] = useState<string | null>(null);

  // PDT-specific hotspots - will be loaded from JSON file
  // Default values as fallback if JSON fails to load
  const [hotspots, setHotspots] = useState([
    // Frontend Layer
    {
      id: 'user',
      x: 1,
      y: 45,
      width: 5,
      height: 10,
      title: 'End User',
      description: 'Personnel accessing the PDT system. End users interact with the system through web browsers to complete position designation workflows.',
      color: 'blue'
    },
    {
      id: 'frontend',
      x: 20,
      y: 42,
      width: 6,
      height: 12,
      title: 'Frontend Container (NGINX + React)',
      description: 'React single-page application served by NGINX web server. Provides the user interface for PDT position designation workflows with form validation and data visualization. Containerized for consistent deployment.',
      color: 'blue'
    },

    // Backend Layer
    {
      id: 'springboot',
      x: 42,
      y: 42,
      width: 10,
      height: 16,
      title: 'Java SpringBoot Backend',
      description: 'Main application server running Java SpringBoot. Handles business logic, data processing, API endpoints, and integration with external systems including DISS.',
      color: 'purple'
    },

    // DISS Integration
    {
      id: 'router',
      x: 43,
      y: 20,
      width: 5,
      height: 8,
      title: 'AWS Transit Gateway (NBIS)',
      description: 'AWS Transit Gateway managed by NBIS for routing traffic between VPCs. Enforces strict network and application security policies while enabling secure communication between PDT VPC and DISS VPC.',
      color: 'purple'
    },
    {
      id: 'diss',
      x: 43,
      y: 8,
      width: 8,
      height: 12,
      title: 'DISS Integration',
      description: 'Defense Information System for Security (DISS) integration. Enables PDT to communicate with DISS for position designation data exchange and validation.',
      color: 'orange'
    },

    // Reporting Pipeline
    {
      id: 'sns',
      x: 58,
      y: 50,
      width: 8,
      height: 10,
      title: 'SNS Topic (Audit Events)',
      description: 'Amazon SNS topic for audit event publishing. Captures all PDT system events including user actions, data changes, and system operations for compliance tracking.',
      color: 'green'
    },
    {
      id: 'firehose',
      x: 70,
      y: 50,
      width: 8,
      height: 10,
      title: 'Kinesis Firehose',
      description: 'Streaming data delivery service. Automatically batches, compresses, and transforms audit events into Parquet format for efficient storage and analytics.',
      color: 'green'
    },
    {
      id: 's3-datalake',
      x: 79,
      y: 50,
      width: 8,
      height: 10,
      title: 'S3 Data Lake (Parquet)',
      description: 'S3-based data lake storing audit events in columnar Parquet format. Partitioned by date for efficient querying and long-term compliance data retention.',
      color: 'green'
    },
    {
      id: 'athena',
      x: 87,
      y: 50,
      width: 7,
      height: 10,
      title: 'Athena (Analytics)',
      description: 'Serverless SQL query engine for data lake analytics. Enables ad-hoc querying and analysis of historical PDT audit data without managing infrastructure.',
      color: 'green'
    },
    {
      id: 'analytics-dashboard',
      x: 93,
      y: 48,
      width: 6,
      height: 12,
      title: 'AI Analytics Dashboard',
      description: 'Executive analytics dashboard providing insights into PDT system usage, position designation trends, compliance metrics, and operational performance.',
      color: 'cyan'
    },

    // Monitoring
    {
      id: 'cloudwatch',
      x: 67,
      y: 78,
      width: 8,
      height: 10,
      title: 'CloudWatch',
      description: 'Centralized logging and monitoring. Aggregates logs from all PDT services, collects metrics, and enables real-time operational visibility and alerting.',
      color: 'cyan'
    },
    {
      id: 'cloudwatch-dashboard',
      x: 82,
      y: 78,
      width: 10,
      height: 10,
      title: 'CloudWatch IT Dashboard',
      description: 'Operational monitoring dashboard for IT teams. Displays real-time metrics including API latency, error rates, SpringBoot performance, and infrastructure health.',
      color: 'cyan'
    }
  ]);

  // Section references for navigation
  const sections = [
    { id: 'overview', title: 'Overview', ref: useRef<HTMLDivElement>(null) },
    { id: 'frontend', title: 'Frontend', ref: useRef<HTMLDivElement>(null) },
    { id: 'backend', title: 'Backend', ref: useRef<HTMLDivElement>(null) },
    { id: 'integration', title: 'DISS Integration', ref: useRef<HTMLDivElement>(null) },
    { id: 'reporting', title: 'Reporting Pipeline', ref: useRef<HTMLDivElement>(null) },
    { id: 'monitoring', title: 'Monitoring', ref: useRef<HTMLDivElement>(null) },
    { id: 'diagram', title: 'Interactive Diagram', ref: useRef<HTMLDivElement>(null) },
  ];

  // Load hotspots from JSON file on component mount
  useEffect(() => {
    const loadHotspots = async () => {
      try {
        const response = await fetch('/pdt-hotspots.json');
        if (response.ok) {
          const data = await response.json();
          setHotspots(data);
          console.log('PDT hotspots loaded from pdt-hotspots.json');
        } else {
          console.warn('Failed to load pdt-hotspots.json, using default hotspots');
        }
      } catch (error) {
        console.error('Error loading hotspots:', error);
        console.warn('Using default hotspots');
      }
    };

    loadHotspots();
  }, []);

  // Scroll to section
  const scrollToSection = (index: number) => {
    const section = sections[index];
    if (section.ref.current) {
      section.ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(index);
    }
  };

  // Navigation
  const navigateToPrevious = () => {
    if (activeSection > 0) {
      scrollToSection(activeSection - 1);
    }
  };

  const navigateToNext = () => {
    if (activeSection < sections.length - 1) {
      scrollToSection(activeSection + 1);
    }
  };

  // Zoom and pan handlers
  const handleWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    e.preventDefault();

    const container = diagramContainerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate zoom
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.min(Math.max(1, zoom * delta), 5);

    // Calculate new pan to keep mouse position stable
    const zoomRatio = newZoom / zoom;
    const newPanX = mouseX - (mouseX - pan.x) * zoomRatio;
    const newPanY = mouseY - (mouseY - pan.y) * zoomRatio;

    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (zoom > 1 && !calibrationMode) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging) {
      setPan({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const resetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const zoomIn = () => {
    const newZoom = Math.min(zoom * 1.2, 5);
    setZoom(newZoom);
  };

  const zoomOut = () => {
    const newZoom = Math.max(zoom * 0.8, 1);
    setZoom(newZoom);
    if (newZoom === 1) {
      setPan({ x: 0, y: 0 });
    }
  };

  // Get color classes for hotspot
  const getHotspotColors = (color: string) => {
    const colorMap: Record<string, { border: string; bg: string; text: string }> = {
      blue: { border: 'border-blue-500', bg: 'bg-blue-500', text: 'text-blue-900' },
      purple: { border: 'border-purple-500', bg: 'bg-purple-500', text: 'text-purple-900' },
      green: { border: 'border-green-500', bg: 'bg-green-500', text: 'text-green-900' },
      orange: { border: 'border-orange-500', bg: 'bg-orange-500', text: 'text-orange-900' },
      cyan: { border: 'border-cyan-500', bg: 'bg-cyan-500', text: 'text-cyan-900' },
    };
    return colorMap[color] || colorMap.blue;
  };

  // Calibration mode handlers
  const toggleCalibrationMode = () => {
    setCalibrationMode(!calibrationMode);
    setEditingHotspot(null);
    setActiveHotspot(null);
  };

  const updateHotspotPosition = (hotspotId: string, field: 'x' | 'y' | 'width' | 'height', value: number) => {
    setHotspots(hotspots.map(h =>
      h.id === hotspotId ? { ...h, [field]: value } : h
    ));
  };

  const exportHotspots = () => {
    const json = JSON.stringify(hotspots, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pdt-hotspots.json';
    a.click();
  };

  return (
    <div className="flex-1 overflow-y-auto bg-gradient-to-br from-gray-50 to-blue-50 relative">
      {/* Slide-out Navigation Panel */}
      <div
        className={`fixed right-0 top-0 h-screen bg-white shadow-2xl transition-all duration-300 ease-in-out z-50 ${
          isNavCollapsed ? 'translate-x-full' : 'translate-x-0'
        }`}
        style={{ width: '320px' }}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-xl font-bold">PDT Architecture</h2>
              <button
                onClick={() => setIsNavCollapsed(true)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
                title="Collapse navigation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            <p className="text-sm text-blue-100">Navigate system components</p>
          </div>

          {/* Section Links */}
          <div className="flex-1 overflow-y-auto p-3">
            <h3 className="text-sm font-semibold text-gray-700 mb-2 px-2">Sections</h3>
            {sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(index)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors border-l-4 rounded-r mb-1 ${
                  activeSection === index
                    ? 'bg-blue-50 border-blue-600 text-blue-900 font-semibold'
                    : 'border-transparent text-gray-700 hover:bg-gray-50'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>

          {/* Bottom Controls */}
          <div className="border-t border-gray-200 p-3 space-y-2">
            <div className="flex gap-2">
              <button
                onClick={navigateToPrevious}
                disabled={activeSection === 0}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
                  activeSection === 0
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                ← Prev
              </button>
              <button
                onClick={navigateToNext}
                disabled={activeSection === sections.length - 1}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors ${
                  activeSection === sections.length - 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tab to open slide-out menu when collapsed */}
      {isNavCollapsed && (
        <button
          onClick={() => setIsNavCollapsed(false)}
          className="fixed right-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-3 py-6 rounded-l-lg shadow-lg hover:from-blue-700 hover:to-purple-700 transition-all z-40"
          title="Open navigation"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div ref={sections[0].ref} id="overview" className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">PDT System Architecture</h1>
          <p className="text-lg text-gray-600">
            Position Designation Tool (PDT) system architecture with DISS integration and compliance reporting
          </p>
        </div>

        {/* Architecture Sections */}
        <div className="space-y-6">
          {/* Frontend Layer */}
          <div ref={sections[1].ref} id="frontend" className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-blue-500">
            <div className="flex items-center mb-4">
              <div className="bg-blue-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Frontend Application</h2>
                <p className="text-gray-600">React-based user interface served by NGINX</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-16">
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">User Access</h3>
                <p className="text-sm text-blue-700">Web-based interface for position designation workflows</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">React Application</h3>
                <p className="text-sm text-blue-700">Single-page application for PDT form management and data entry</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">NGINX Server</h3>
                <p className="text-sm text-blue-700">High-performance web server for React application delivery</p>
              </div>
            </div>
          </div>

          {/* Backend Layer */}
          <div ref={sections[2].ref} id="backend" className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-purple-500">
            <div className="flex items-center mb-4">
              <div className="bg-purple-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Java SpringBoot Backend</h2>
                <p className="text-gray-600">Enterprise application server handling business logic</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 ml-16">
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-900 mb-2">API Endpoints</h3>
                <p className="text-sm text-purple-700">RESTful APIs for position designation operations</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-900 mb-2">Business Logic</h3>
                <p className="text-sm text-purple-700">Position designation rules and validation processing</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-semibold text-purple-900 mb-2">Data Processing</h3>
                <p className="text-sm text-purple-700">Event publishing and audit trail generation</p>
              </div>
            </div>
          </div>

          {/* DISS Integration */}
          <div ref={sections[3].ref} id="integration" className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-orange-500">
            <div className="flex items-center mb-4">
              <div className="bg-orange-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">DISS Integration</h2>
                <p className="text-gray-600">Defense Information System for Security connectivity</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-16">
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold text-orange-900 mb-2">AWS Transit Gateway (NBIS)</h3>
                <p className="text-sm text-orange-700">Secure inter-VPC routing with strict network and application security policies</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg">
                <h3 className="font-semibold text-orange-900 mb-2">Data Exchange</h3>
                <p className="text-sm text-orange-700">Position designation data synchronization with DISS</p>
              </div>
            </div>
          </div>

          {/* Reporting Pipeline */}
          <div ref={sections[4].ref} id="reporting" className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-green-500">
            <div className="flex items-center mb-4">
              <div className="bg-green-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Reporting Pipeline</h2>
                <p className="text-gray-600">Automated audit event processing and analytics</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 ml-16">
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-2">SNS Topic</h3>
                <p className="text-sm text-green-700">Event publishing for audit trails</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-2">Kinesis Firehose</h3>
                <p className="text-sm text-green-700">Streaming data transformation to Parquet</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-2">S3 Data Lake</h3>
                <p className="text-sm text-green-700">Long-term storage in columnar format</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-2">Athena Analytics</h3>
                <p className="text-sm text-green-700">SQL queries on historical data</p>
              </div>
            </div>
          </div>

          {/* Monitoring */}
          <div ref={sections[5].ref} id="monitoring" className="bg-white rounded-lg shadow-lg p-6 border-l-4 border-cyan-500">
            <div className="flex items-center mb-4">
              <div className="bg-cyan-100 p-3 rounded-lg mr-4">
                <svg className="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Monitoring & Observability</h2>
                <p className="text-gray-600">CloudWatch-based system monitoring</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 ml-16">
              <div className="bg-cyan-50 p-4 rounded-lg">
                <h3 className="font-semibold text-cyan-900 mb-2">CloudWatch Logs</h3>
                <p className="text-sm text-cyan-700">Centralized logging from all system components</p>
              </div>
              <div className="bg-cyan-50 p-4 rounded-lg">
                <h3 className="font-semibold text-cyan-900 mb-2">IT Dashboard</h3>
                <p className="text-sm text-cyan-700">Real-time operational metrics and alerts</p>
              </div>
            </div>
          </div>

          {/* Interactive Architecture Diagram */}
          <div ref={sections[6].ref} id="diagram" className="bg-gradient-to-br from-slate-50 to-indigo-50 rounded-lg shadow-xl p-6 border-2 border-indigo-300 mt-8">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="bg-indigo-100 p-3 rounded-lg mr-4">
                  <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Interactive Architecture Diagram</h2>
                  <p className="text-gray-600">Click components to learn more. Use mouse wheel to zoom.</p>
                </div>
              </div>

              {/* Diagram Controls */}
              <div className="flex items-center gap-2">
                <button
                  onClick={toggleCalibrationMode}
                  className={`px-3 py-1.5 text-sm font-medium rounded transition-colors ${
                    calibrationMode
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                  title={calibrationMode ? 'Exit calibration mode' : 'Enter calibration mode'}
                >
                  {calibrationMode ? 'Exit Calibration' : 'Calibrate'}
                </button>
                {calibrationMode && (
                  <button
                    onClick={exportHotspots}
                    className="px-3 py-1.5 text-sm font-medium bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                    title="Export hotspot positions"
                  >
                    Export
                  </button>
                )}
                <button
                  onClick={zoomOut}
                  className="p-2 bg-white rounded-lg shadow hover:bg-gray-50 transition-colors"
                  title="Zoom out"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                  </svg>
                </button>
                <button
                  onClick={zoomIn}
                  className="p-2 bg-white rounded-lg shadow hover:bg-gray-50 transition-colors"
                  title="Zoom in"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                  </svg>
                </button>
                <button
                  onClick={resetZoom}
                  className="p-2 bg-white rounded-lg shadow hover:bg-gray-50 transition-colors"
                  title="Reset zoom"
                >
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Zoom indicator */}
            <div className="mb-4 flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Zoom: {(zoom * 100).toFixed(0)}%
              </div>
              {calibrationMode && (
                <div className="text-sm text-red-600 font-medium">
                  CALIBRATION MODE: Click and drag hotspots to adjust positions
                </div>
              )}
            </div>

            {/* Diagram Container */}
            <div
              className="relative bg-white rounded-lg border-2 border-gray-300"
              style={{ height: '600px', position: 'relative', overflow: 'hidden' }}
            >
              <div
                ref={diagramContainerRef}
                style={{
                  width: '100%',
                  height: '100%',
                  cursor: zoom > 1 && !calibrationMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  position: 'relative',
                }}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
              >
                {/* Diagram Image */}
                <div
                  style={{
                    transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                    transformOrigin: '0 0',
                    transition: isDragging ? 'none' : 'transform 0.1s',
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <img
                    src="/PDT-ArchitectureDiagram.png"
                    alt="PDT System Architecture Diagram"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      pointerEvents: 'none',
                      userSelect: 'none',
                    }}
                    draggable={false}
                  />

                  {/* Interactive Hotspots */}
                  {hotspots.map((hotspot) => {
                    const colors = getHotspotColors(hotspot.color);
                    const isActive = activeHotspot === hotspot.id;
                    const isEditing = editingHotspot === hotspot.id;

                    return (
                      <div
                        key={hotspot.id}
                        onMouseEnter={() => {
                          // Only allow hover if not in calibration mode
                          if (!calibrationMode) {
                            setActiveHotspot(hotspot.id);
                          }
                        }}
                        onMouseLeave={() => {
                          // Only clear on hover leave if not in calibration mode
                          if (!calibrationMode) {
                            setActiveHotspot(null);
                          }
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (calibrationMode) {
                            setEditingHotspot(isEditing ? null : hotspot.id);
                          }
                        }}
                        style={{
                          position: 'absolute',
                          left: `${hotspot.x}%`,
                          top: `${hotspot.y}%`,
                          width: `${hotspot.width}%`,
                          height: `${hotspot.height}%`,
                          pointerEvents: 'auto',
                          cursor: calibrationMode ? 'move' : 'pointer',
                          zIndex: isActive || isEditing ? 20 : 10
                        }}
                        className="group"
                      >
                        {/* Hotspot Border */}
                        <div
                          className={`w-full h-full border-2 rounded-lg transition-all duration-200 ${
                            isActive || isEditing
                              ? `${colors.border} bg-white bg-opacity-20 shadow-lg border-4`
                              : 'border-transparent hover:border-gray-400 hover:bg-white hover:bg-opacity-10'
                          }`}
                        />

                        {/* Calibration mode indicator */}
                        {isEditing && (
                          <div className="absolute inset-0 rounded-lg">
                            <div className={`absolute inset-0 ${colors.border} rounded-lg animate-ping opacity-40`}></div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Tooltips - rendered outside overflow container */}
              {hotspots.map((hotspot) => {
                const colors = getHotspotColors(hotspot.color);
                const isActive = activeHotspot === hotspot.id;

                if (!isActive) return null;

                // Position tooltips at bottom for components near top
                const positionBelow = hotspot.y < 30;

                // Position to the right or left based on horizontal position
                const positionRight = hotspot.x < 50;

                return (
                  <div
                    key={`tooltip-${hotspot.id}`}
                    className="absolute pointer-events-none"
                    style={{
                      left: positionRight ? `calc(${hotspot.x + hotspot.width}% + 8px)` : 'auto',
                      right: !positionRight ? `calc(${100 - hotspot.x}% + 8px)` : 'auto',
                      top: positionBelow ? `${hotspot.y}%` : 'auto',
                      bottom: !positionBelow ? `calc(100% - ${hotspot.y}% + 8px)` : 'auto',
                      zIndex: 50,
                      minWidth: '280px',
                      maxWidth: '320px'
                    }}
                  >
                    <div className={`bg-white rounded-lg shadow-2xl border-2 ${colors.border} overflow-hidden`}>
                      <div className={`${colors.bg} bg-opacity-10 px-4 py-3 border-b ${colors.border}`}>
                        <h4 className={`font-bold ${colors.text} flex items-center`}>
                          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                          {hotspot.title}
                        </h4>
                      </div>
                      <div className="px-4 py-3 bg-white">
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {hotspot.description}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Calibration Controls */}
            {calibrationMode && editingHotspot && (
              <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg">
                <h3 className="font-semibold text-yellow-900 mb-3">
                  Editing: {hotspots.find(h => h.id === editingHotspot)?.title}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {(['x', 'y', 'width', 'height'] as const).map(field => {
                    const hotspot = hotspots.find(h => h.id === editingHotspot);
                    return (
                      <div key={field}>
                        <label className="block text-xs font-medium text-yellow-900 mb-1 capitalize">
                          {field}: {hotspot?.[field].toFixed(1)}%
                        </label>
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="0.1"
                          value={hotspot?.[field] || 0}
                          onChange={(e) => updateHotspotPosition(editingHotspot, field, parseFloat(e.target.value))}
                          className="w-full"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Legend */}
            <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">Component Legend</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
                  <span className="text-gray-700">Frontend Layer</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-purple-500 rounded mr-2"></div>
                  <span className="text-gray-700">Backend Layer</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-orange-500 rounded mr-2"></div>
                  <span className="text-gray-700">DISS Integration</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                  <span className="text-gray-700">Reporting Pipeline</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 bg-cyan-500 rounded mr-2"></div>
                  <span className="text-gray-700">Monitoring</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemArchitecturePDT;
